import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBack from "@mui/icons-material/ArrowBack";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import axios from "axios";
import { buildJsonBody } from "../../utils/encryption";
import { formatValidationError } from "../../utils/formatValidation";
import { formatPasswordNotStrongEnough } from "../../utils/passwordPolicy";
import PasswordStrengthHints from "./PasswordStrengthHints";
import {
  getApiBase,
  AUTH_SIGNUP,
  AUTH_LOGIN,
  AUTH_VERIFY_CODE,
  AUTH_RESEND_CODE,
  AUTH_FORGOT_PASSWORD,
  AUTH_SET_PASSWORD,
  AUTH_ME,
} from "../../config/api";
import {
  isChatbotMaintenanceMode,
  MAINTENANCE_PAGE_PATH,
} from "../../config/features";

const API_BASE = getApiBase();

/** Site AppBar uses z-index 1301; modal must sit above it with room below the bar. */
const AUTH_MODAL_Z_INDEX = 1400;
const AUTH_MODAL_TOP_OFFSET = {
  xs: "calc(64px + env(safe-area-inset-top, 0px) + 12px)",
  sm: "calc(72px + env(safe-area-inset-top, 0px) + 16px)",
};
const AUTH_MODAL_MAX_HEIGHT = {
  xs: "calc(100dvh - 64px - env(safe-area-inset-top, 0px) - 24px)",
  sm: "calc(100dvh - 72px - env(safe-area-inset-top, 0px) - 32px)",
};

function setTokens({ access_token, refresh_token, user_id }) {
  if (access_token) localStorage.setItem("authToken", access_token);
  if (refresh_token) localStorage.setItem("refreshToken", refresh_token);
  if (user_id) localStorage.setItem("user_id", user_id);
  localStorage.setItem("authTimestamp", String(Date.now()));
}

async function signup(body) {
  const { data } = await axios.post(`${API_BASE}${AUTH_SIGNUP}`, buildJsonBody(body));
  return data;
}
async function login(body) {
  const { data } = await axios.post(`${API_BASE}${AUTH_LOGIN}`, buildJsonBody(body));
  return data;
}
async function verifyCode(body) {
  const { data } = await axios.post(`${API_BASE}${AUTH_VERIFY_CODE}`, buildJsonBody(body));
  return data;
}
async function resendCode(userEmail) {
  const { data } = await axios.post(`${API_BASE}${AUTH_RESEND_CODE}`, buildJsonBody({ user_email: userEmail }));
  return data;
}
async function getMe(token) {
  const { data } = await axios.get(`${API_BASE}${AUTH_ME}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}
async function forgotPassword(userEmail) {
  await axios.post(`${API_BASE}${AUTH_FORGOT_PASSWORD}`, buildJsonBody({ user_email: userEmail }));
}
async function setPasswordApi(newPassword, accessToken) {
  const { data } = await axios.post(
    `${API_BASE}${AUTH_SET_PASSWORD}`,
    { new_password: newPassword },
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
  return data;
}

/* ── Reusable styled input ── */
const StyledInput = ({ label, icon: Icon, InputProps: extraInputProps, ...props }) => (
  <TextField
    label={label}
    fullWidth
    size="small"
    {...props}
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "14px",
        background: "#f8fafc",
        fontSize: "0.88rem",
        "& fieldset": { borderColor: "rgba(15,23,42,0.1)" },
        "&:hover fieldset": { borderColor: "rgba(25,118,210,0.4)" },
        "&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: "1.5px" },
      },
      "& .MuiInputLabel-root": {
        fontSize: "0.82rem",
        color: "#94a3b8",
        "&.Mui-focused": { color: "#1976d2" },
      },
      ...props.sx,
    }}
    InputProps={{
      startAdornment: Icon ? (
        <InputAdornment position="start">
          <Icon sx={{ fontSize: 17, color: "#94a3b8" }} />
        </InputAdornment>
      ) : undefined,
      ...extraInputProps,
    }}
  />
);

/* ── Primary CTA button ── */
const PrimaryBtn = ({ children, loading, ...props }) => (
  <Button
    variant="contained"
    size="large"
    fullWidth
    disabled={loading || props.disabled}
    {...props}
    sx={{
      background: "linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #42a5f5 100%)",
      borderRadius: "14px",
      py: 1.45,
      fontSize: "0.9rem",
      fontWeight: 700,
      textTransform: "none",
      letterSpacing: "0.01em",
      boxShadow: "0 4px 18px rgba(25,118,210,0.35)",
      transition: "all 0.25s ease",
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 60%, #1976d2 100%)",
        boxShadow: "0 8px 28px rgba(25,118,210,0.45)",
        transform: "translateY(-1px)",
      },
      "&:disabled": { opacity: 0.6 },
      ...props.sx,
    }}
  >
    {loading ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={15} color="inherit" />
        {children}
      </Box>
    ) : children}
  </Button>
);

const AuthModal = ({ open, onClose, defaultView = "signIn" }) => {
  const navigate = useNavigate();
  const [view, setView] = useState(defaultView);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [fromForgotPassword, setFromForgotPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNew, setShowConfirmNew] = useState(false);
  const [tempAccessToken, setTempAccessToken] = useState("");
  const [tempUserData, setTempUserData] = useState(null);

  React.useEffect(() => { if (open) setView(defaultView); }, [open, defaultView]);
  React.useEffect(() => {
    if (!open || !isChatbotMaintenanceMode) return;
    onClose?.();
    if (window.location.pathname !== MAINTENANCE_PAGE_PATH) {
      navigate(MAINTENANCE_PAGE_PATH);
    }
  }, [open, onClose, navigate]);
  React.useEffect(() => {
    if (!open) {
      setEmail(""); setPassword(""); setConfirmPassword("");
      setError(""); setSuccess("");
      setShowPassword(false); setShowConfirm(false);
      setLoading(false); setPendingEmail("");
      setVerificationCode(""); setView(defaultView);
      setFromForgotPassword(false);
      setNewPassword(""); setConfirmNewPassword("");
      setShowNewPassword(false); setShowConfirmNew(false);
      setTempAccessToken(""); setTempUserData(null);
    }
  }, [open, defaultView]);

  const setAuthState = (user) => {
    try {
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("authUser", JSON.stringify(user));
      localStorage.setItem("authTimestamp", String(Date.now()));
      if (user?.user_id) localStorage.setItem("user_id", user.user_id);
    } catch (_) { }
    window.dispatchEvent(new CustomEvent("auth-changed", { detail: { isAuthenticated: true, user } }));
    setSuccess("Signed in! You may now use the chatbot.");
    onClose && onClose();
  };

  const handleSignUp = async () => {
    setError("");
    const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/i;
    if (!email || !password || !confirmPassword) { setError("Please fill all fields."); return; }
    if (!emailRegex.test(email)) { setError("Please enter a valid email address."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      const data = await signup({ user_email: email, user_password: password });
      if (data.requires_verification) {
        setPendingEmail(data.user_email || email);
        setVerificationCode(""); setView("verifyCode");
      }
    } catch (err) {
      const raw = err.response?.data?.detail ?? err.response?.data?.message;
      setError(formatValidationError(raw, "Unable to create account."));
    } finally { setLoading(false); }
  };

  const handleVerifyCode = async () => {
    setError("");
    const code = verificationCode.trim();
    if (!code || code.length !== 6) { setError("Please enter the 6-digit code."); return; }
    setLoading(true);
    try {
      const data = await verifyCode({ user_email: pendingEmail, verification_code: code });
      if (!data?.access_token) { setError("Verification failed. Please try again."); return; }
      setTokens({ access_token: data.access_token, refresh_token: data.refresh_token, user_id: data.user_id });
      let user = { email: pendingEmail, user_id: data.user_id };
      try {
        const profile = await getMe(data.access_token);
        user = { email: profile.user_email, user_id: profile.user_id, api_key: profile.api_key };
      } catch { }
      setAuthState(user);
    } catch (err) {
      const raw = err.response?.data?.detail ?? err.response?.data?.message;
      setError(formatValidationError(raw, "Verification failed."));
    } finally { setLoading(false); }
  };

  const handleResendCode = async () => {
    setError(""); setLoading(true);
    try {
      const data = await resendCode(pendingEmail);
      setSuccess(data.message || "A new code has been sent.");
    } catch (err) {
      const raw = err.response?.data?.detail ?? err.response?.data?.message;
      setError(formatValidationError(raw, "Unable to resend code."));
    } finally { setLoading(false); }
  };

  const handleSignIn = async () => {
    setError("");
    if (!email || !password) { setError("Please enter email and password."); return; }
    setLoading(true);
    try {
      const data = await login({ user_email: email, user_password: password });
      if (!data?.access_token) { setError("Unable to sign in."); return; }
      setTokens({ access_token: data.access_token, refresh_token: data.refresh_token, user_id: data.user_id });
      let user = { email, user_id: data.user_id };
      try {
        const profile = await getMe(data.access_token);
        user = { email: profile.user_email, user_id: profile.user_id, api_key: profile.api_key };
      } catch (_) { }

      if (fromForgotPassword) {
        setTempAccessToken(data.access_token);
        setTempUserData(user);
        setNewPassword(""); setConfirmNewPassword("");
        setError("");
        setView("setNewPassword");
        return;
      }

      setAuthState(user);
    } catch (err) {
      const raw = err.response?.data?.detail ?? err.response?.data?.message;
      setError(formatValidationError(raw, "Unable to sign in."));
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async () => {
    setError("");
    if (!email) { setError("Please enter your email address."); return; }
    setLoading(true);
    try {
      await forgotPassword(email);
      setFromForgotPassword(true);
      setPassword("");
      setSuccess("A temporary password has been sent to your email. Please sign in with it.");
      setView("signIn");
    } catch (err) {
      const raw = err.response?.data?.detail ?? err.response?.data?.message;
      setError(formatValidationError(raw, "Something went wrong."));
    } finally { setLoading(false); }
  };

  const handleSetNewPassword = async () => {
    setError("");
    if (!newPassword || !confirmNewPassword) { setError("Please fill in both fields."); return; }
    const newPwPolicyErr = formatPasswordNotStrongEnough(newPassword);
    if (newPwPolicyErr) { setError(newPwPolicyErr); return; }
    if (newPassword !== confirmNewPassword) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await setPasswordApi(newPassword, tempAccessToken);
      setFromForgotPassword(false);
      setAuthState(tempUserData);
      setSuccess("Password updated successfully!");
    } catch (err) {
      const raw = err.response?.data?.detail ?? err.response?.data?.message;
      setError(formatValidationError(raw, "Unable to update password."));
    } finally { setLoading(false); }
  };

  const eyeBtn = (visible, toggle) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={toggle} onMouseDown={(e) => e.preventDefault()} edge="end" size="small">
          {visible
            ? <VisibilityOff sx={{ fontSize: 17, color: "#94a3b8" }} />
            : <Visibility sx={{ fontSize: 17, color: "#94a3b8" }} />}
        </IconButton>
      </InputAdornment>
    ),
  });

  const headerTitles = {
    signIn: { title: "Welcome back", sub: "Sign in to access TekkDev AI Chatbot" },
    signUp: { title: "Create account", sub: "Join TekkDev AI — it's free" },
    verifyCode: { title: "Check your inbox", sub: `We sent a code to ${pendingEmail}` },
    forgotPassword: { title: "Reset password", sub: "We'll email you a new password" },
    setNewPassword: { title: "Set new password", sub: "Choose a secure password for your account" },
  };
  const h = headerTitles[view] || headerTitles.signIn;

  const handleDialogClose = (event, reason) => {
    if (
      view === "setNewPassword" &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    ) {
      return;
    }
    onClose && onClose();
  };

  /** Temp-password login already stored tokens; closing here must still sync app auth (Header / chatbot). */
  const handleHeaderClose = () => {
    if (view === "setNewPassword" && tempUserData) {
      setFromForgotPassword(false);
      setAuthState(tempUserData);
      return;
    }
    onClose && onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
        disableRestoreFocus
        sx={{
          zIndex: AUTH_MODAL_Z_INDEX,
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            justifyContent: "center",
            pt: AUTH_MODAL_TOP_OFFSET,
            pb: { xs: 1.5, sm: 2 },
            px: { xs: 1, sm: 2 },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            overflow: "hidden",
            my: 0,
            maxHeight: AUTH_MODAL_MAX_HEIGHT,
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 40px 100px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.05)",
            background: "#fff",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(5,10,24,0.6)",
            // backdropFilter: "blur(10px)",  Dont use it !
          },
        }}
      >
        {/* ── Header banner ── */}
        <Box
          sx={{
            position: "relative",
            background: "linear-gradient(135deg, #0c0c1d 0%, #1a1a3e 45%, #0d47a1 100%)",
            px: 4,
            pt: 4,
            pb: 3.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            overflow: "hidden",
            flexShrink: 0,
            // subtle grid overlay
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
              pointerEvents: "none",
            },
          }}
        >
          {/* Close btn */}
          <IconButton
            onClick={handleHeaderClose}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 2,
              color: "rgba(255,255,255,0.5)",
              background: "rgba(255,255,255,0.07)",
              width: 30,
              height: 30,
              "&:hover": { background: "rgba(255,255,255,0.14)", color: "#fff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Bot icon */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              width: 54,
              height: 54,
              borderRadius: "16px",
              background: "linear-gradient(135deg, rgba(66,165,245,0.28), rgba(25,118,210,0.5))",
              border: "1px solid rgba(255,255,255,0.14)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.8,
              boxShadow: "0 8px 24px rgba(25,118,210,0.35)",
            }}
          >
            <SmartToyOutlinedIcon sx={{ fontSize: 27, color: "#fff" }} />
          </Box>

          <Typography
            sx={{
              position: "relative",
              zIndex: 1,
              fontWeight: 800,
              fontSize: "1.22rem",
              color: "#fff",
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            {h.title}
          </Typography>
          <Typography
            sx={{
              position: "relative",
              zIndex: 1,
              fontSize: "0.76rem",
              color: "rgba(255,255,255,0.5)",
              fontWeight: 400,
            }}
          >
            {h.sub}
          </Typography>
        </Box>

        {/* ── Body ── */}
        <DialogContent
          sx={{
            px: 3.5,
            pt: 3,
            pb: view === "setNewPassword" ? 4.5 : 3.5,
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            boxSizing: "border-box",
            // Keep scrollbar (and its down arrow) above the paper's rounded bottom edge
            mb: 1.25,
            mr: 0.25,
            borderBottomLeftRadius: "24px",
            borderBottomRightRadius: "24px",
          }}
        >

          {/* Tab switcher */}
          {(view === "signIn" || view === "signUp") && (
            <Box
              sx={{
                display: "flex",
                background: "#f1f5f9",
                borderRadius: "14px",
                p: "4px",
                mb: 2.5,
              }}
            >
              {[["signIn", "Sign In"], ["signUp", "Sign Up"]].map(([v, label]) => (
                <Box
                  key={v}
                  onClick={() => { setView(v); setError(""); }}
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    py: 0.85,
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    fontFamily: "'Sora', sans-serif",
                    transition: "all 0.2s ease",
                    background: view === v ? "#fff" : "transparent",
                    color: view === v ? "#0f172a" : "#94a3b8",
                    boxShadow: view === v ? "0 2px 8px rgba(0,0,0,0.09)" : "none",
                    userSelect: "none",
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          )}

          {/* Error box */}
          {error && (
            <Box
              sx={{
                mb: 2,
                px: 2,
                py: 1.1,
                borderRadius: "10px",
                background: "rgba(239,68,68,0.07)",
                border: "1px solid rgba(239,68,68,0.18)",
              }}
            >
              <Typography sx={{ fontSize: "0.77rem", color: "#dc2626", fontWeight: 500 }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* ── SIGN IN ── */}
          {view === "signIn" && (
            <Stack
              component="form"
              spacing={2}
              onSubmit={(e) => {
                e.preventDefault();
                void handleSignIn();
              }}
            >
              <StyledInput
                label="Email address"
                type="email"
                icon={EmailOutlinedIcon}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <StyledInput
                label="Password"
                type={showPassword ? "text" : "password"}
                icon={LockOutlinedIcon}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                // extraInputProps={eyeBtn(showPassword, () => setShowPassword((s) => !s))}  WTF 
                InputProps={eyeBtn(showPassword, () => setShowPassword((s) => !s))}
              />
              <Box sx={{ textAlign: "right", mt: -0.5 }}>
                <Typography
                  component="span"
                  onClick={() => { setView("forgotPassword"); setError(""); }}
                  sx={{ fontSize: "0.74rem", color: "#1976d2", cursor: "pointer", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}
                >
                  Forgot password?
                </Typography>
              </Box>
              <PrimaryBtn loading={loading} type="submit">Sign In</PrimaryBtn>
              <Typography sx={{ textAlign: "center", fontSize: "0.77rem", color: "#94a3b8", pt: 0.5 }}>
                Don't have an account?{" "}
                <Typography component="span" onClick={() => setView("signUp")}
                  sx={{ color: "#1976d2", fontWeight: 600, fontSize: "0.72rem", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                  Sign up free
                </Typography>
              </Typography>
            </Stack>
          )}

          {/* ── SIGN UP ── */}
          {view === "signUp" && (
            <Stack
              component="form"
              spacing={2}
              onSubmit={(e) => {
                e.preventDefault();
                void handleSignUp();
              }}
            >
              <StyledInput
                label="Email address"
                type="email"
                icon={EmailOutlinedIcon}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
              />
              <StyledInput
                label="Password"
                type={showPassword ? "text" : "password"}
                icon={LockOutlinedIcon}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                InputProps={eyeBtn(showPassword, () => setShowPassword((s) => !s))}
              />
              <PasswordStrengthHints password={password} sx={{ mt: -0.5 }} />
              <StyledInput
                label="Confirm Password"
                type={showConfirm ? "text" : "password"}
                icon={LockOutlinedIcon}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                InputProps={eyeBtn(showConfirm, () => setShowConfirm((s) => !s))}
              />
              <PrimaryBtn loading={loading} type="submit">Create Account</PrimaryBtn>
              <Typography sx={{ textAlign: "center", fontSize: "0.77rem", color: "#94a3b8", pt: 0.5 }}>
                Already have an account?{" "}
                <Typography component="span" onClick={() => setView("signIn")}
                  sx={{ color: "#1976d2", fontWeight: 600, fontSize: "0.72rem", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                  Sign in
                </Typography>
              </Typography>
            </Stack>
          )}

          {/* ── VERIFY CODE ── */}
          {view === "verifyCode" && (
            <Stack
              component="form"
              spacing={2.5}
              alignItems="center"
              onSubmit={(e) => {
                e.preventDefault();
                void handleVerifyCode();
              }}
            >
              <Box sx={{
                width: 50, height: 50, borderRadius: "14px",
                background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem",
              }}>
                📧
              </Box>
              <Typography sx={{ textAlign: "center", fontSize: "0.81rem", color: "#64748b", lineHeight: 1.65 }}>
                Enter the 6-digit code sent to{" "}
                <Typography component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>{pendingEmail}</Typography>
              </Typography>
              <TextField
                fullWidth size="small"
                value={verificationCode}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setVerificationCode(v); setError("");
                }}
                inputProps={{
                  maxLength: 6, inputMode: "numeric",
                  style: {
                    textAlign: "center", letterSpacing: "0.65em",
                    fontSize: "1.55rem", fontWeight: 800,
                    fontFamily: "'Sora', sans-serif",
                    color: "#0f172a", paddingLeft: "0.65em",
                  },
                }}
                placeholder="——————"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px", background: "#f8fafc",
                    "& fieldset": { borderColor: "rgba(15,23,42,0.1)" },
                    "&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: "1.5px" },
                  },
                }}
              />
              <PrimaryBtn loading={loading} type="submit" disabled={loading || verificationCode.length !== 6}>
                Verify & Sign In
              </PrimaryBtn>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Typography component="span" onClick={handleResendCode}
                  sx={{ fontSize: "0.74rem", color: "#1976d2", cursor: "pointer", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}>
                  Resend code
                </Typography>
                <Typography component="span" onClick={() => { setView("signUp"); setError(""); setVerificationCode(""); }}
                  sx={{ fontSize: "0.74rem", color: "#94a3b8", cursor: "pointer", "&:hover": { color: "#64748b" } }}>
                  ← Back to Sign Up
                </Typography>
              </Box>
            </Stack>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {view === "forgotPassword" && (
            <Stack
              component="form"
              spacing={2}
              onSubmit={(e) => {
                e.preventDefault();
                void handleForgotPassword();
              }}
            >
              <Typography sx={{ fontSize: "0.81rem", color: "#64748b", lineHeight: 1.65 }}>
                Enter your email and we'll send you a temporary password if an account exists.
              </Typography>
              <StyledInput
                label="Email address"
                type="email"
                icon={EmailOutlinedIcon}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
              />
              <PrimaryBtn loading={loading} type="submit">Send Reset Email</PrimaryBtn>
              <Box
                onClick={() => { setView("signIn"); setError(""); setFromForgotPassword(false); }}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.4,
                  cursor: "pointer", color: "#94a3b8", fontSize: "0.76rem",
                  fontWeight: 500, width: "fit-content",
                  "&:hover": { color: "#64748b" },
                }}
              >
                <ArrowBack sx={{ fontSize: 13 }} />
                Back to Sign In
              </Box>
            </Stack>
          )}

          {/* ── SET NEW PASSWORD ── */}
          {view === "setNewPassword" && (
            <Stack
              component="form"
              spacing={2.5}
              alignItems="center"
              onSubmit={(e) => {
                e.preventDefault();
                void handleSetNewPassword();
              }}
            >
              <Box sx={{
                width: 50, height: 50, borderRadius: "14px",
                background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem",
              }}>
                🔒
              </Box>
              <Typography sx={{ textAlign: "center", fontSize: "0.81rem", color: "#64748b", lineHeight: 1.65 }}>
                Your temporary password worked! Now set a new permanent password for your account.
              </Typography>
              <StyledInput
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                icon={LockOutlinedIcon}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
                InputProps={eyeBtn(showNewPassword, () => setShowNewPassword((s) => !s))}
              />
              <PasswordStrengthHints password={newPassword} sx={{ width: "100%", mt: -0.5 }} />
              <StyledInput
                label="Confirm New Password"
                type={showConfirmNew ? "text" : "password"}
                icon={LockOutlinedIcon}
                value={confirmNewPassword}
                onChange={(e) => { setConfirmNewPassword(e.target.value); setError(""); }}
                InputProps={eyeBtn(showConfirmNew, () => setShowConfirmNew((s) => !s))}
              />
              <PrimaryBtn
                loading={loading}
                type="submit"
                disabled={loading || !newPassword || !confirmNewPassword}
              >
                Update Password
              </PrimaryBtn>
              <Box aria-hidden sx={{ height: 14, flexShrink: 0 }} />
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={3000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")} sx={{ borderRadius: "12px", fontWeight: 500 }}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AuthModal;